/**
 * ShouldITradeCard -- trade-readiness summary used on Trade Signals (equity
 * options) + Stock Signals pages.
 *
 * Driven primarily by macro state (combined_penalty + combined_regime). When
 * an optional `regime` prop is provided (GEX/COT tags from
 * /market/regime), the card upgrades to a richer 4-row Q&A: market state +
 * direction lean + action + preset hint. Without it, the card still works
 * macro-only.
 *
 * The Notches page has its own internal richer variant that also handles
 * per-symbol regime + skew. This shared component covers the simpler cases.
 */
import React from 'react';
import { MacroState } from '../services/tradeSignalsService';
import { MarketRegime } from '../services/marketRegimeService';

type SafetyRating = 'good' | 'okay' | 'mixed' | 'caution' | 'wait';

const C = {
  card:    '#1f2937',
  cardAlt: '#1a2332',
  border:  '#374151',
  text:    '#f9fafb',
  muted:   '#9ca3af',
  dim:     '#6b7280',
  green:   '#16a34a',
  greenBg: '#16a34a20',
  amber:   '#d97706',
  amberBg: '#d9770620',
  red:     '#dc2626',
  redBg:   '#dc262620',
};

const SAFETY: Record<SafetyRating, { color: string; bg: string; label: string }> = {
  // Labels mirror MacroStressPanel HEADER_LABEL so both cards stay in sync.
  good:    { color: C.green, bg: C.greenBg, label: 'Yes — favorable setup'        },
  okay:    { color: C.green, bg: C.greenBg, label: 'Yes, but be selective'        },
  mixed:   { color: C.amber, bg: C.amberBg, label: 'Trade smaller — best setups only' },
  caution: { color: C.amber, bg: C.amberBg, label: 'Limited data — be careful'    },
  wait:    { color: C.red,   bg: C.redBg,   label: 'No — better to wait'          },
};

// Plain-English market state inferred from GEX tag. Strategy-agnostic.
function marketStateFromGex(gexTag: string | null | undefined): string | null {
  switch (gexTag) {
    case 'POS_GEX_ABOVE_FLIP': return 'Calm and steady';
    case 'POS_GEX_BELOW_FLIP': return 'Recovering after a recent dip';
    case 'NEAR_GAMMA_FLIP':    return 'Unsettled — could move either way';
    case 'NEG_GEX_BELOW_FLIP': return 'Falling, could fall further';
    case 'NEG_GEX_ABOVE_FLIP': return 'Rising, could rise sharply';
    default:                   return null;
  }
}

// Tactical bias label, mirroring the SMS tripwire vocabulary in
// notches-spot-tripwire/handler.js decode(). Surfaces the per-symbol read
// alongside the macro-driven verdict so the card and the SMS use the same
// words even when they reach different overall conclusions.
type TacticalBias = { label: string; color: string };
function tacticalBiasFromGex(gexTag: string | null | undefined): TacticalBias | null {
  switch (gexTag) {
    case 'POS_GEX_ABOVE_FLIP': return { label: 'LONG — sell puts or go long, normal size',  color: '#15803d' };
    case 'POS_GEX_BELOW_FLIP': return { label: 'LONG-DIP — sell puts or go long',           color: '#16a34a' };
    case 'NEAR_GAMMA_FLIP':    return { label: 'CHOP — cut new size in half',               color: '#ca8a04' };
    case 'NEG_GEX_BELOW_FLIP': return { label: 'SHORT-DRIFT — no new short puts',           color: '#ea580c' };
    case 'NEG_GEX_ABOVE_FLIP': return { label: 'STAND DOWN — cascade risk',                 color: '#dc2626' };
    default:                   return null;
  }
}

// True when the macro-driven verdict says wait/caution but the per-symbol
// tactical bias is bullish (LONG / LONG-DIP). The most common live mismatch
// and the one that needs an explicit reading rule.
function isWaitButLong(safety: SafetyRating, gexTag: string | null | undefined): boolean {
  if (safety !== 'wait' && safety !== 'caution') return false;
  return gexTag === 'POS_GEX_ABOVE_FLIP' || gexTag === 'POS_GEX_BELOW_FLIP';
}

// Direction lean from COT tag. Plain-English label only -- the contrarian
// reasoning lives in whyDirection() for the expanded "why" panel.
function directionFromCot(cotTag: string | null | undefined): string | null {
  switch (cotTag) {
    case 'LM_EXTREME_LONG':    return 'Downward bias';
    case 'LM_STRETCHED_LONG':  return 'Slight downward lean';
    case 'LM_NEUTRAL':         return 'No clear direction';
    case 'LM_STRETCHED_SHORT': return 'Slight upward lean';
    case 'LM_EXTREME_SHORT':   return 'Upward bias';
    default:                   return null;
  }
}

// Plain-English explanation of WHY each tag implies the direction lean.
// Shown in the expanded "Why?" panel only.
function whyMarketState(gexTag: string | null | undefined): string | null {
  switch (gexTag) {
    case 'POS_GEX_ABOVE_FLIP': return 'Market structure is supportive right now — price moves tend to stay contained.';
    case 'POS_GEX_BELOW_FLIP': return 'The market recently dipped, but the structure tends to pull prices back toward the recent range.';
    case 'NEAR_GAMMA_FLIP':    return 'The market is at a level where moves can accelerate quickly in either direction.';
    case 'NEG_GEX_BELOW_FLIP': return 'Market structure is amplifying downward moves — falls can extend rather than reverse.';
    case 'NEG_GEX_ABOVE_FLIP': return 'Market structure is amplifying upward moves — rallies can extend rather than fade.';
    default:                   return null;
  }
}

function whyDirection(cotTag: string | null | undefined): string | null {
  switch (cotTag) {
    case 'LM_EXTREME_LONG':    return 'The market is more likely to go DOWN than up. Almost all big traders are already betting on a rise, and historically when the crowd gets this one-sided, prices turn the other way.';
    case 'LM_STRETCHED_LONG':  return 'The market is leaning slightly DOWN. Many big traders are already betting on a rise, and historically when too many traders agree, the opposite move often follows.';
    case 'LM_NEUTRAL':         return 'No clear direction. Big traders are split between bullish and bearish bets — no strong crowd signal either way.';
    case 'LM_STRETCHED_SHORT': return 'The market is leaning slightly UP. Many big traders are already betting on a fall, and historically when too many traders agree, the opposite move often follows.';
    case 'LM_EXTREME_SHORT':   return 'The market is more likely to go UP than down. Almost all big traders are already betting on a fall, and historically when the crowd gets this one-sided, prices turn the other way.';
    default:                   return null;
  }
}

// 2026-06-27 -- coordinated with MacroStressPanel. The top-line verdict is
// now derived 1:1 from macro.combined_regime so the two cards never
// contradict (previous composite blend with GEX/COT could push the verdict
// to 'wait' even when macro alone said 'stressed', confusing users).
// GEX/COT remain visible in the card's expanded Q&A as direction/positioning
// context -- they no longer override the macro-driven sizing tier.
function deriveSafety(combinedRegime: string | null | undefined): SafetyRating {
  switch (combinedRegime) {
    case 'calm':     return 'good';
    case 'cautious': return 'okay';
    case 'stressed': return 'mixed';
    case 'extreme':  return 'wait';
    default:         return 'caution'; // unknown / missing data -- be careful, not a green light
  }
}

function actionLineFor(safety: SafetyRating, tradeNoun: string): string {
  switch (safety) {
    case 'good':    return `Take ${tradeNoun} at normal size.`;
    case 'okay':    return `Take ${tradeNoun}, but stick to your best setups.`;
    case 'mixed':   return `Trade smaller than usual; only your best setups.`;
    case 'caution': return `Take only your best setups at smaller size. Check back later — once this turns favorable, conditions have improved.`;
    case 'wait':    return `Avoid ${tradeNoun}. Check back later — once this turns favorable, conditions have improved.`;
  }
}

function presetForSafety(safety: SafetyRating): string {
  switch (safety) {
    case 'good':    return 'Aggressive sizing is reasonable.';
    case 'okay':    return 'Stick to normal sizing.';
    case 'mixed':   return 'Be conservative on size.';
    case 'caution': return 'Conservative sizing only.';
    case 'wait':    return 'Skip for now.';
  }
}

// Plain-English macro explanation that names the specific factor(s) driving
// the read. Combined tier = worst-of-four; we list every factor sitting at
// that tier so subscribers know exactly what is moving.
const FACTOR_LABEL: Record<string, string> = {
  oil: 'Oil', gold: 'Gold', bonds: 'Long bonds', rates: 'Interest rates',
};

function namedFactorsAtTier(macro: MacroState, tier: string): string {
  const keys: Array<keyof MacroState> = ['oil', 'gold', 'bonds', 'rates'] as any;
  const matches = keys
    .map((k) => ((macro as any)[k]?.regime === tier ? FACTOR_LABEL[k as string] : null))
    .filter(Boolean) as string[];
  if (matches.length === 0) return '';
  if (matches.length === 1) return matches[0];
  if (matches.length === 2) return `${matches[0]} and ${matches[1]}`;
  return `${matches.slice(0, -1).join(', ')}, and ${matches[matches.length - 1]}`;
}

function whyMacro(macro: MacroState): string {
  const tier = macro?.combined_regime;
  if (!tier || tier === 'calm') {
    return 'Oil, gold, and bond markets are quiet — no extra caution needed.';
  }
  const names = namedFactorsAtTier(macro, tier);
  const subject = names || 'One macro factor';
  const isPlural = names.includes(',') || names.includes(' and ');
  const verb = isPlural ? 'are' : 'is';
  switch (tier) {
    case 'cautious':
      return `${subject} ${verb} showing a slightly larger move than usual.`;
    case 'stressed':
      return `${subject} ${verb} showing a notably large move today. Larger-than-usual swings often follow.`;
    case 'extreme':
      return `${subject} ${verb} showing a tail-event-sized move. The broader market often reacts sharply when this happens.`;
    default:
      return '';
  }
}

interface Props {
  macro: MacroState;
  regime?: MarketRegime | null;
  tradeNoun?: string;
}

export const ShouldITradeCard: React.FC<Props> = ({ macro, regime, tradeNoun = 'new trades' }) => {
  const safety = deriveSafety(macro?.combined_regime);
  const meta = SAFETY[safety];
  const action = actionLineFor(safety, tradeNoun);
  const preset = presetForSafety(safety);
  const why = whyMacro(macro);
  const marketState = marketStateFromGex(regime?.gex_tag);
  const direction = directionFromCot(regime?.cot_tag);
  const whyMarket = whyMarketState(regime?.gex_tag);
  const whyDir = whyDirection(regime?.cot_tag);
  const hasRegime = !!(regime && (regime.gex_tag || regime.cot_tag));
  const tactical = tacticalBiasFromGex(regime?.gex_tag);
  const showReadingRule = isWaitButLong(safety, regime?.gex_tag);

  return (
    <div style={{
      background: C.card,
      border: `1px solid ${C.border}`,
      borderRadius: 8,
      padding: '0.7rem 1rem',
      marginBottom: '0.6rem',
    }}>
      <details>
        <summary style={{ cursor: 'pointer', listStyle: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.55rem' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <h3 style={{ margin: 0, fontSize: '0.95rem', color: C.text }}>Should I trade right now?</h3>
            <span style={{ fontSize: '0.66rem', color: C.muted }}>▼ details</span>
          </span>
          <span style={{
            display: 'inline-block',
            padding: '0.18rem 0.55rem',
            borderRadius: 12,
            background: meta.bg,
            color: meta.color,
            fontSize: '0.74rem',
            fontWeight: 700,
          }}>
            {meta.label}
          </span>
        </summary>

        <div style={{ marginTop: '0.6rem', borderTop: `1px solid ${C.border}`, paddingTop: '0.5rem' }}>
          <Row q="What to do" a={action} bold />
          {marketState && <Row q="Where is the market?" a={marketState} />}
          {direction   && <Row q="Where is it going?"   a={direction} />}
          {tactical    && <Row q="Tactical bias right now" a={tactical.label} color={tactical.color} />}
          <Row q="Aggressive or conservative?" a={preset} />

          {showReadingRule && (
            <div style={{
              marginTop: '0.55rem',
              fontSize: '0.78rem',
              color: C.text,
              lineHeight: 1.55,
              padding: '0.6rem 0.75rem',
              background: '#0f172a',
              border: `1px solid ${C.amber}`,
              borderRadius: 6,
            }}>
              <div style={{ marginBottom: '0.5rem' }}>
                <strong style={{ color: C.amber }}>How to read this:</strong> portfolio risk says wait, but the tactical bias is supportive. <strong>Don't open new positions.</strong> For trades you already hold, lean with the tactical bias on management decisions.
              </div>
              <div style={{ fontSize: '0.74rem', color: C.muted, lineHeight: 1.6 }}>
                <div style={{ color: C.text, fontWeight: 600, marginBottom: '0.25rem' }}>Example — short put with bias LONG:</div>
                <div>• <strong>Stops:</strong> give it more room before bailing — recovery is likely</div>
                <div>• <strong>Rolls:</strong> if forced to roll, push the strike DOWN (more cushion)</div>
                <div>• <strong>Profits:</strong> let winners run a bit longer; don't take profit early</div>
                <div style={{ marginTop: '0.35rem', fontStyle: 'italic', color: C.dim }}>
                  If the bias flips to SHORT, do the opposite: tighten stops, close winners faster.
                </div>
              </div>
            </div>
          )}

          <div style={{
            marginTop: '0.55rem',
            fontSize: '0.78rem',
            color: C.muted,
            lineHeight: 1.55,
            padding: '0.55rem 0.7rem',
            background: C.cardAlt,
            border: `1px solid ${C.border}`,
            borderRadius: 6,
          }}>
            {whyMarket && <div style={{ marginBottom: '0.4rem' }}>{whyMarket}</div>}
            {whyDir    && <div style={{ marginBottom: '0.4rem' }}>{whyDir}</div>}
            {whyDir && (
              <details style={{ marginBottom: '0.4rem' }}>
                <summary style={{ cursor: 'pointer', fontSize: '0.72rem', color: C.muted, fontWeight: 600 }}>
                  How does this work?
                </summary>
                <div style={{ marginTop: '0.35rem', fontSize: '0.76rem', color: C.muted, lineHeight: 1.55 }}>
                  <p style={{ margin: '0 0 0.45rem' }}>
                    If most traders are already betting prices will <strong>fall</strong>, there is almost nobody left to push prices lower. Eventually those traders will need to <strong>buy back</strong> what they sold short — that buying pushes prices UP.
                  </p>
                  <p style={{ margin: '0 0 0.45rem' }}>
                    The same logic in reverse: when most traders are already betting on a <strong>rise</strong>, nobody is left to push prices higher, and longs eventually have to sell to take profits — pushing prices DOWN.
                  </p>
                  <p style={{ margin: 0, color: C.dim, fontStyle: 'italic' }}>
                    This isn't a guarantee. Sometimes the crowd is right and prices keep going their way. But CFTC positioning data going back decades shows that extreme one-sided positioning tends to reverse within weeks more often than not.
                  </p>
                </div>
              </details>
            )}
            {why && <div style={{ marginBottom: '0.35rem' }}>{why}</div>}
            {hasRegime && (
              <div style={{ color: C.dim, fontSize: '0.72rem', fontStyle: 'italic' }}>
                {regime?.cot_source === 'ES (futures proxy)'
                  ? 'Market read uses SPY (broad market) and ES futures (positioning).'
                  : 'Market read is from SPY (broad-market proxy).'}
              </div>
            )}
            <div style={{ marginTop: '0.3rem', color: C.dim, fontSize: '0.72rem', fontStyle: 'italic' }}>
              This is the current setup. If conditions change, refresh the page — once the badge above turns favorable, conditions have improved.
            </div>
          </div>
        </div>
      </details>
    </div>
  );
};

const Row: React.FC<{ q: string; a: string; bold?: boolean; color?: string }> = ({ q, a, bold, color }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: 'minmax(140px, 32%) 1fr',
    gap: '0.85rem',
    padding: '0.32rem 0',
    borderBottom: `1px solid ${C.border}40`,
  }}>
    <div style={{ fontSize: '0.74rem', color: C.muted }}>{q}</div>
    <div style={{ fontSize: '0.84rem', color: color || C.text, fontWeight: bold || color ? 700 : 500 }}>{a}</div>
  </div>
);
